<template>
  <!-- 🔹 MOBILE BACKDROP OVERLAY (Teleported to body with z-[60] to cover sticky headers & page content) -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="internalIsOpenMobile"
        class="fixed inset-0 z-[60] bg-slate-950/60 backdrop-blur-sm md:hidden"
        aria-hidden="true"
        @click.stop="closeMobile"
      ></div>
    </Transition>
  </Teleport>

  <!-- 🔹 FLOATING FLYOUT / TOOLTIP FOR COLLAPSED SIDEBAR (Teleported to body to escape overflow clipping) -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isDesktopCollapsed && activeHoverItem"
        :style="floatingFlyoutStyle"
        class="-translate-y-1/2 pointer-events-auto"
        :dir="lang === 'ar' ? 'rtl' : 'ltr'"
        @mouseenter="handleFlyoutMouseEnter"
        @mouseleave="handleFlyoutMouseLeave"
      >
        <!-- Profile / Single Tooltip -->
        <div
          v-if="activeHoverItem.type === 'single' || activeHoverItem.type === 'profile'"
          class="relative flex items-center gap-2.5 px-3 py-2 text-xs font-semibold rounded-xl shadow-xl bg-slate-900/95 text-white dark:bg-slate-800/95 dark:text-slate-100 backdrop-blur-md ring-1 ring-white/10 dark:ring-slate-700/60 whitespace-nowrap"
        >
          <!-- Arrow pointer -->
          <span
            class="absolute top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-slate-900 dark:bg-slate-800 ring-1 ring-white/10 dark:ring-slate-700/60"
            :class="lang === 'ar' ? '-right-1 border-t border-r' : '-left-1 border-b border-l'"
          ></span>

          <i
            v-if="activeHoverItem.icon"
            :class="activeHoverItem.icon"
            class="text-[17px] text-primary"
          ></i>

          <div class="flex flex-col">
            <span class="font-semibold leading-tight">{{ activeHoverItem.label }}</span>
            <span
              v-if="activeHoverItem.subLabel"
              class="text-[11px] text-slate-400 font-normal mt-0.5 leading-none"
            >
              {{ activeHoverItem.subLabel }}
            </span>
          </div>
        </div>

        <!-- Grouped Menu Flyout -->
        <div
          v-else-if="activeHoverItem.type === 'group'"
          class="relative min-w-[210px] max-w-[270px] p-2 rounded-2xl shadow-2xl bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-white backdrop-blur-md ring-1 ring-slate-200/80 dark:ring-slate-700/80"
        >
          <!-- Arrow pointer -->
          <span
            class="absolute top-4 w-2.5 h-2.5 rotate-45 bg-white dark:bg-slate-900 ring-1 ring-slate-200/80 dark:ring-slate-700/80"
            :class="lang === 'ar' ? '-right-1.5 border-t border-r' : '-left-1.5 border-b border-l'"
          ></span>

          <!-- Group Header -->
          <div class="flex items-center gap-2 px-2.5 py-2 mb-1.5 border-b border-slate-100 dark:border-slate-800">
            <i :class="activeHoverItem.icon" class="text-[18px] text-primary shrink-0"></i>
            <span class="text-xs font-bold text-slate-900 dark:text-white truncate">
              {{ activeHoverItem.label }}
            </span>
          </div>

          <!-- Group Children Links -->
          <div class="space-y-1">
            <RouterLink
              v-for="(child, ci) in activeHoverItem.children"
              :key="'flyout-sub-' + ci"
              :to="child.route || '#'"
              class="flex items-center gap-2.5 px-2.5 py-2 text-xs font-medium rounded-xl transition-colors duration-150"
              :class="[
                $route.path === child.route
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-secondary/10 hover:text-primary'
              ]"
              @click="closeFlyoutAndNavigate"
            >
              <i :class="child.icon" class="text-[17px] shrink-0"></i>
              <span class="truncate">{{ t(child.labelKey) }}</span>
            </RouterLink>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- 🔹 SIDEBAR ROOT CONTAINER -->
  <aside
    ref="sidebarRef"
    :dir="lang === 'ar' ? 'rtl' : 'ltr'"
    :role="isMobileState ? 'dialog' : 'navigation'"
    :aria-modal="isMobileState ? 'true' : undefined"
    :aria-expanded="isMobileState ? internalIsOpenMobile : undefined"
    :aria-hidden="isMobileState ? !internalIsOpenMobile : undefined"
    :aria-label="t('sidebar_brand') || 'القائمة الرئيسية'"
    :class="sidebarClasses"
    @keydown="handleKeydown"
  >
    <!-- 🔹 HEADER SECTION -->
    <header
      class="shrink-0 border-b border-slate-200/80 dark:border-slate-700/80 bg-gradient-to-b from-primary/[0.06] via-secondary/[0.03] to-transparent dark:from-primary/10 dark:via-secondary/5"
    >
      <div
        class="pointer-events-none h-0.5 bg-gradient-to-r from-primary via-secondary to-secondary/50 opacity-80"
      ></div>

      <!-- EXPANDED HEADER (Desktop Expanded OR Mobile Drawer) -->
      <template v-if="!isDesktopCollapsed">
        <div class="flex items-center justify-between px-4 pt-4 pb-2.5">
          <RouterLink
            to="/consultations/dashboard"
            class="flex items-center min-w-0"
            :title="t('sidebar_brand')"
            @click="closeMobile"
          >
            <img
              :src="sidebarLogo"
              :alt="t('sidebar_brand')"
              class="h-12 w-auto max-w-[12rem] object-contain"
              :class="lang === 'ar' ? 'object-right' : 'object-left'"
              loading="eager"
              decoding="async"
            />
          </RouterLink>

          <!-- Mobile Close Button -->
          <button
            type="button"
            class="md:hidden p-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary shrink-0"
            aria-label="إغلاق القائمة"
            @click.stop="closeMobile"
          >
            <i class="mdi mdi-close text-2xl"></i>
          </button>
        </div>

        <!-- User profile summary card (Expanded) -->
        <RouterLink
          to="/account/profile"
          class="group relative flex items-center gap-3 px-4 py-3 transition-colors bg-secondary/[0.06] dark:bg-secondary/10 hover:bg-secondary/10 dark:hover:bg-secondary/20"
          :title="displayName"
          @click="closeMobile"
        >
          <div
            class="pointer-events-none absolute inset-y-2.5 w-0.5 rounded-full"
            :class="[roleTheme.accent, lang === 'ar' ? 'right-3' : 'left-3']"
          ></div>

          <div
            class="relative shrink-0 flex items-center justify-center w-11 h-11 overflow-hidden rounded-xl shadow-sm ring-1 ring-black/[0.04] dark:ring-white/10 transition-transform duration-200 group-hover:scale-[1.03]"
            :class="roleTheme.avatarFill"
          >
            <img
              v-if="avatarSrc"
              :src="avatarSrc"
              :alt="displayName"
              class="object-cover w-full h-full"
            />
            <span v-else-if="userInitials" class="text-sm font-bold tracking-wide">
              {{ userInitials }}
            </span>
            <i v-else :class="roleTheme.icon" class="text-xl"></i>
          </div>

          <div
            class="min-w-0 flex-1"
            :class="lang === 'ar' ? 'pr-1' : 'pl-1'"
          >
            <p
              class="truncate text-sm font-semibold tracking-tight text-slate-900 dark:text-white"
              dir="auto"
            >
              {{ displayName }}
            </p>
            <p
              class="truncate text-xs font-medium mt-0.5"
              :class="roleTheme.roleText"
              dir="auto"
            >
              {{ displayRole }}
            </p>
          </div>

          <i
            class="mdi mdi-chevron-right shrink-0 text-base text-slate-400 opacity-0 transition-all duration-200 group-hover:opacity-100"
            :class="lang === 'ar' ? 'rotate-180' : 'group-hover:translate-x-0.5'"
          ></i>
        </RouterLink>
      </template>

      <!-- COLLAPSED HEADER (Desktop Collapsed only) -->
      <template v-else>
        <div class="flex flex-col items-center gap-3 px-3 py-4">
          <RouterLink
            to="/consultations/dashboard"
            class="flex w-full items-center justify-center px-1 pt-1"
            :title="t('sidebar_brand')"
            @mouseenter="handleItemMouseEnter($event, { id: 'brand', labelKey: 'sidebar_brand' }, 'single')"
            @mouseleave="handleItemMouseLeave"
            @focus="handleItemMouseEnter($event, { id: 'brand', labelKey: 'sidebar_brand' }, 'single')"
            @blur="handleItemMouseLeave"
          >
            <img
              :src="sidebarLogo"
              :alt="t('sidebar_brand')"
              class="h-10 w-auto max-w-[3.5rem] object-contain object-center"
              loading="eager"
              decoding="async"
            />
          </RouterLink>

          <RouterLink
            to="/account/profile"
            class="group relative flex justify-center"
            :title="displayName"
            @mouseenter="handleItemMouseEnter($event, { id: 'profile', labelKey: displayName, subLabel: displayRole }, 'profile')"
            @mouseleave="handleItemMouseLeave"
            @focus="handleItemMouseEnter($event, { id: 'profile', labelKey: displayName, subLabel: displayRole }, 'profile')"
            @blur="handleItemMouseLeave"
          >
            <div
              class="flex items-center justify-center w-10 h-10 overflow-hidden rounded-xl shadow-sm ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 transition-transform duration-200 group-hover:scale-[1.05]"
              :class="[roleTheme.avatarFill, roleTheme.ring]"
            >
              <img
                v-if="avatarSrc"
                :src="avatarSrc"
                :alt="displayName"
                class="object-cover w-full h-full"
              />
              <span v-else-if="userInitials" class="text-xs font-bold">
                {{ userInitials }}
              </span>
              <i v-else :class="roleTheme.icon" class="text-base"></i>
            </div>
          </RouterLink>
        </div>
      </template>
    </header>

    <!-- 🔹 NAVIGATION MENU -->
    <nav
      :dir="lang === 'ar' ? 'rtl' : 'ltr'"
      class="flex-1 min-h-0 px-2 py-3 space-y-1 overflow-y-auto overflow-x-hidden sidebar-scroll custom-scrollbar"
    >
      <div
        v-for="(group, gi) in filteredNavItems"
        :key="'group-' + gi"
        class="mb-2"
      >
        <!-- Single top-level item -->
        <template v-if="!group.children">
          <!-- EXPANDED ITEM -->
          <RouterLink
            v-if="!isDesktopCollapsed"
            :to="group.route || '#'"
            class="flex items-center w-full px-3 py-2.5 text-sm font-semibold transition-all duration-200 border rounded-xl"
            :class="[
              $route.path === group.route
                ? 'bg-primary/10 text-primary shadow-sm border-primary/30'
                : 'border-transparent text-slate-700 dark:text-slate-200 hover:bg-secondary/10 hover:text-primary',
            ]"
            @click="closeMobile"
          >
            <span class="flex items-center gap-2.5 min-w-0">
              <i :class="group.icon" class="text-[20px] shrink-0"></i>
              <span class="truncate">{{ t(group.labelKey) }}</span>
            </span>
          </RouterLink>

          <!-- COLLAPSED ITEM -->
          <RouterLink
            v-else
            :to="group.route || '#'"
            class="relative flex items-center justify-center w-full h-11 p-2.5 transition-all duration-200 rounded-2xl group"
            :class="[
              $route.path === group.route
                ? 'bg-secondary/15 text-primary shadow-sm ring-1 ring-secondary/25 dark:bg-slate-800 dark:text-primary dark:ring-slate-700/60'
                : 'text-slate-600 dark:text-slate-300 hover:bg-secondary/15 hover:text-primary hover:shadow-sm hover:ring-1 hover:ring-secondary/20 dark:hover:bg-slate-800 dark:hover:text-primary',
            ]"
            @mouseenter="handleItemMouseEnter($event, group, 'single')"
            @mouseleave="handleItemMouseLeave"
            @focus="handleItemMouseEnter($event, group, 'single')"
            @blur="handleItemMouseLeave"
          >
            <!-- Black Shape Indicator on Hover / Active -->
            <span
              class="absolute -start-2 top-1/2 -translate-y-1/2 w-2.5 h-7 bg-[#161c2d] dark:bg-slate-100 rounded-e-md shadow-sm pointer-events-none transition-all duration-200 ease-out"
              :class="[
                $route.path === group.route
                  ? 'opacity-100 scale-100 translate-x-0'
                  : 'opacity-0 scale-y-50 group-hover:opacity-100 group-hover:scale-100',
                lang === 'ar'
                  ? ($route.path === group.route ? '' : 'translate-x-1.5 group-hover:translate-x-0')
                  : ($route.path === group.route ? '' : '-translate-x-1.5 group-hover:translate-x-0')
              ]"
            ></span>

            <i :class="group.icon" class="text-[20px] transition-transform duration-200 group-hover:scale-110"></i>
          </RouterLink>
        </template>

        <!-- Grouped dropdown item -->
        <template v-else>
          <!-- EXPANDED GROUP BUTTON -->
          <button
            v-if="!isDesktopCollapsed"
            type="button"
            class="flex items-center justify-between w-full px-3 py-2.5 text-sm font-semibold transition rounded-xl text-slate-700 dark:text-slate-200 hover:text-primary hover:bg-secondary/10"
            @click="toggleGroup(group.id)"
          >
            <span class="flex items-center gap-2.5 min-w-0">
              <i :class="group.icon" class="text-[20px] text-primary shrink-0"></i>
              <span class="truncate">{{ t(group.labelKey) }}</span>
            </span>

            <i
              class="mdi text-[18px] text-slate-400 transition-transform shrink-0"
              :class="
                openGroups[group.id] ? 'mdi-chevron-up' : 'mdi-chevron-down'
              "
            ></i>
          </button>

          <!-- COLLAPSED GROUP BUTTON -->
          <button
            v-else
            type="button"
            class="relative flex items-center justify-center w-full h-11 p-2.5 transition-all duration-200 rounded-2xl group"
            :class="[
              shouldOpenGroup(group)
                ? 'bg-secondary/15 text-primary shadow-sm ring-1 ring-secondary/25 dark:bg-slate-800 dark:text-primary dark:ring-slate-700/60'
                : 'text-slate-600 dark:text-slate-300 hover:bg-secondary/15 hover:text-primary hover:shadow-sm hover:ring-1 hover:ring-secondary/20 dark:hover:bg-slate-800 dark:hover:text-primary',
            ]"
            :title="t(group.labelKey)"
            @click="toggleGroup(group.id)"
            @mouseenter="handleItemMouseEnter($event, group, 'group')"
            @mouseleave="handleItemMouseLeave"
            @focus="handleItemMouseEnter($event, group, 'group')"
            @blur="handleItemMouseLeave"
          >
            <!-- Black Shape Indicator on Hover / Active -->
            <span
              class="absolute -start-2 top-1/2 -translate-y-1/2 w-2.5 h-7 bg-[#161c2d] dark:bg-slate-100 rounded-e-md shadow-sm pointer-events-none transition-all duration-200 ease-out"
              :class="[
                shouldOpenGroup(group)
                  ? 'opacity-100 scale-100 translate-x-0'
                  : 'opacity-0 scale-y-50 group-hover:opacity-100 group-hover:scale-100',
                lang === 'ar'
                  ? (shouldOpenGroup(group) ? '' : 'translate-x-1.5 group-hover:translate-x-0')
                  : (shouldOpenGroup(group) ? '' : '-translate-x-1.5 group-hover:translate-x-0')
              ]"
            ></span>

            <i :class="group.icon" class="text-[20px] transition-transform duration-200 group-hover:scale-110"></i>
          </button>

          <!-- EXPANDED GROUP SUBLINKS -->
          <div
            v-if="!isDesktopCollapsed"
            v-show="openGroups[group.id]"
            class="mt-1 space-y-1"
            :class="lang === 'ar' ? 'pr-3 border-r-2 border-slate-200/60 dark:border-slate-800' : 'pl-3 border-l-2 border-slate-200/60 dark:border-slate-800'"
          >
            <RouterLink
              v-for="(item, i) in group.children"
              :key="group.id + '-' + i"
              :to="item.route || '#'"
              class="flex items-center w-full px-3 py-2 text-sm transition-all duration-200 border rounded-xl"
              :class="[
                $route.path === item.route
                  ? 'bg-primary/10 text-primary font-semibold shadow-sm border-primary/30'
                  : 'border-transparent text-slate-600 dark:text-slate-300 hover:bg-secondary/10 hover:text-primary',
              ]"
              @click="closeMobile"
            >
              <span class="flex items-center gap-2.5 min-w-0">
                <i :class="item.icon" class="text-[18px] shrink-0"></i>
                <span class="truncate">{{ t(item.labelKey) }}</span>
              </span>
            </RouterLink>
          </div>

          <!-- COLLAPSED GROUP SUBLINKS -->
          <div
            v-else
            v-show="openGroups[group.id]"
            class="mt-1 space-y-1"
          >
            <RouterLink
              v-for="(item, i) in group.children"
              :key="group.id + '-collapsed-' + i"
              :to="item.route || '#'"
              class="relative flex items-center justify-center w-full h-11 p-2.5 transition-all duration-200 rounded-2xl group"
              :class="[
                $route.path === item.route
                  ? 'bg-secondary/15 text-primary shadow-sm ring-1 ring-secondary/25 dark:bg-slate-800 dark:text-primary dark:ring-slate-700/60'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-secondary/15 hover:text-primary hover:shadow-sm hover:ring-1 hover:ring-secondary/20 dark:hover:bg-slate-800 dark:hover:text-primary',
              ]"
              @mouseenter="handleItemMouseEnter($event, item, 'single')"
              @mouseleave="handleItemMouseLeave"
              @focus="handleItemMouseEnter($event, item, 'single')"
              @blur="handleItemMouseLeave"
            >
              <!-- Black Shape Indicator on Hover / Active -->
              <span
                class="absolute -start-2 top-1/2 -translate-y-1/2 w-2.5 h-7 bg-[#161c2d] dark:bg-slate-100 rounded-e-md shadow-sm pointer-events-none transition-all duration-200 ease-out"
                :class="[
                  $route.path === item.route
                    ? 'opacity-100 scale-100 translate-x-0'
                    : 'opacity-0 scale-y-50 group-hover:opacity-100 group-hover:scale-100',
                  lang === 'ar'
                    ? ($route.path === item.route ? '' : 'translate-x-1.5 group-hover:translate-x-0')
                    : ($route.path === item.route ? '' : '-translate-x-1.5 group-hover:translate-x-0')
                ]"
              ></span>

              <i :class="item.icon" class="text-[18px] transition-transform duration-200 group-hover:scale-110"></i>
            </RouterLink>
          </div>
        </template>
      </div>
    </nav>
  </aside>

  <!-- Theme Switcher -->
  <div class="fixed z-30 top-1/4" :class="lang === 'ar' ? 'left-3' : 'right-3'">
    <button
      type="button"
      @click="toggleTheme"
      class="relative inline-flex items-center justify-between w-16 px-1 rounded-full shadow-md h-9 bg-white/90 dark:bg-slate-900/90 backdrop-blur ring-1 ring-slate-200 dark:ring-slate-700"
      aria-label="تبديل النمط"
    >
      <i class="text-xl mdi mdi-white-balance-sunny text-primary"></i>
      <i class="text-xl mdi mdi-weather-night text-primary"></i>

      <span
        class="absolute transition-transform duration-300 rounded-full shadow-sm top-1 left-1 size-7 bg-secondary"
        :class="isDark ? 'translate-x-7' : 'translate-x-0'"
      ></span>
    </button>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import logoImg from "@/assets/images/logo.png";
import logoLightImg from "@/assets/images/logo-light.png";

// ====== Props & Emits ======
const props = defineProps({
  navItems: {
    type: Array,
    default: () => [
      {
        id: "home",
        icon: "mdi mdi-home-outline",
        labelKey: "sidebar_item_home",
        route: "/consultations/dashboard",
      },
      {
        id: "user-profile",
        icon: "mdi mdi-account-circle-outline",
        labelKey: "sidebar_item_profile",
        route: "/account/profile",
      },
      {
        id: "office-management",
        icon: "mdi mdi-office-building-marker-outline",
        labelKey: "sidebar_item_office_management",
        route: "/offices",
      },
      {
        id: "consultations",
        icon: "mdi mdi-forum-outline",
        labelKey: "sidebar_group_consultations",
        children: [
          {
            id: "consultation-create",
            icon: "mdi mdi-plus-circle-outline",
            labelKey: "sidebar_item_create",
            route: "/consultations/create",
          },
          {
            id: "consultation-my",
            icon: "mdi mdi-clipboard-text-outline",
            labelKey: "sidebar_item_my_consultations",
            route: "/consultations/my",
          },
          {
            id: "consultation-coming-session",
            icon: "mdi mdi-calendar-clock-outline",
            labelKey: "sidebar_item_coming_session_consultations",
            route: "/my-consultations/coming/session",
          },
          {
            id: "consultation-coming-chat",
            icon: "mdi mdi-chat-processing-outline",
            labelKey: "sidebar_item_coming_chat_consultations",
            route: "/my-consultations/coming/chat",
          },
        ],
      },
      {
        id: "tickets",
        icon: "mdi mdi-ticket-confirmation-outline",
        labelKey: "sidebar_group_tickets",
        children: [
          {
            id: "tickets-my",
            icon: "mdi mdi-ticket-outline",
            labelKey: "sidebar_item_my_tickets",
            route: "/tickets/my",
          },
          {
            id: "tickets-create",
            icon: "mdi mdi-plus-circle-outline",
            labelKey: "sidebar_item_create_ticket",
            route: "/tickets/create",
          },
        ],
      },
    ],
  },
  isCollapsed: { type: Boolean, default: false },
  isOpenMobile: { type: Boolean, default: false },
  isSidebarOpenMobile: { type: Boolean, default: false },
  modelValue: { type: Boolean, default: false },
  sidebarSearch: { type: String, default: "" },
  lang: { type: String, default: "ar" },
  t: { type: Function, required: true },
});

const emit = defineEmits([
  "update:sidebar-search",
  "update:is-open-mobile",
  "update:isOpenMobile",
  "update:isSidebarOpenMobile",
  "update:modelValue",
  "close-mobile",
  "close",
]);

// 🔹 Prop Aliases
const navItems = computed(() => props.navItems || []);
const isCollapsed = computed(() => props.isCollapsed);
const isOpenMobile = computed(() => props.isOpenMobile || props.isSidebarOpenMobile || props.modelValue);
const lang = computed(() => props.lang || "ar");
const t = (key) => (typeof props.t === "function" ? props.t(key) : key);

// 🔹 Internal Reactive Mobile State (Instant UI close & full sync)
const internalIsOpenMobile = ref(Boolean(props.isOpenMobile || props.isSidebarOpenMobile || props.modelValue));

watch(
  () => [props.isOpenMobile, props.isSidebarOpenMobile, props.modelValue],
  () => {
    internalIsOpenMobile.value = Boolean(
      props.isOpenMobile || props.isSidebarOpenMobile || props.modelValue
    );
  },
  { immediate: true }
);

// ====== Router ======
const route = useRoute();
const router = useRouter();

// ====== State ======
const openGroups = reactive({
  consultations: true,
});
const isDark = ref(false);
const user = ref(null);

const sidebarLogo = computed(() => {
  // Colored logo reads clearer on the light-tinted header strip
  if (!isDark.value) return logoImg;
  return logoLightImg;
});

// ====== Helpers: cookies ======
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) {
    user.value = null;
    return;
  }

  try {
    user.value = JSON.parse(raw);
  } catch (e) {
    console.error("Failed to parse auth_user cookie", e);
    user.value = null;
  }
}

const localSearch = computed({
  get: () => props.sidebarSearch,
  set: (val) => emit("update:sidebar-search", val),
});

const rawRole = computed(() =>
  String(user.value?.role || user.value?.type || "").toLowerCase()
);

const isOfficeConsulting = computed(() => {
  const r = rawRole.value;
  return (
    r.includes("office-consulting") ||
    r.includes("office_consulting") ||
    r.includes("consultant-office") ||
    r.includes("consultant_office") ||
    (r.includes("consultant") && r.includes("office")) ||
    r === "office"
  );
});

const isConsultantOnly = computed(() => {
  if (isOfficeConsulting.value) return false;
  const r = rawRole.value;
  return r.includes("consultant");
});

const isClientRole = computed(() => {
  const role = rawRole.value;
  return (
    role === "client" ||
    role === "customer" ||
    role === "requester" ||
    role.includes("client")
  );
});

const isConsultantOfficeRole = computed(() => {
  const role = rawRole.value;
  return (
    role.includes("consultant-office") ||
    role.includes("consultant_office") ||
    (role.includes("consultant") && role.includes("office")) ||
    role === "office"
  );
});

const isConsultantRole = computed(() => {
  if (isConsultantOfficeRole.value) return false;
  const role = rawRole.value;
  return role.includes("consultant") || role.includes("U.O3O¦O'OOñ");
});

const displayName = computed(() => {
  if (isClientRole.value) {
    return (
      user.value?.client?.name ||
      user.value?.name ||
      user.value?.email ||
      "User"
    );
  }

  if (isConsultantOfficeRole.value) {
    return (
      user.value?.consultant?.office_name ||
      user.value?.consultant?.name ||
      user.value?.name ||
      user.value?.email ||
      "User"
    );
  }

  if (isConsultantRole.value) {
    return (
      user.value?.consultant?.name ||
      user.value?.name ||
      user.value?.email ||
      "User"
    );
  }

  return user.value?.name || user.value?.email || "User";
});

const displayRole = computed(() => {
  if (isClientRole.value) return t("role_client");
  if (isConsultantOfficeRole.value) return t("role_consultant_office");
  if (isConsultantRole.value) return t("role_consultant");
  return user.value?.role || "role";
});

const settingsRoute = computed(() => {
  if (isClientRole.value) return "/account/settings/client";
  if (isConsultantOfficeRole.value) return "/account/settings/office";
  if (isConsultantRole.value) return "/account/settings/consultant";
  return "/account/settings/client";
});

const userInitials = computed(() => {
  const name = displayName.value;
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  const first = (parts[0] || "").charAt(0);
  const second = (parts[1] || "").charAt(0);
  const initials = (first + second).trim();
  return (initials || first).toUpperCase();
});

const avatarSrc = computed(() => {
  if (!user.value) return "";

  if (isClientRole.value) {
    return user.value?.client?.avatar || user.value?.avatar || "";
  }

  if (isConsultantOfficeRole.value || isConsultantRole.value) {
    return (
      user.value?.consultant?.logo ||
      user.value?.office?.logo ||
      user.value?.avatar ||
      ""
    );
  }

  return user.value?.avatar || user.value?.office?.logo || "";
});

const roleTheme = computed(() => {
  if (isClientRole.value) {
    return {
      icon: "mdi mdi-account-outline",
      avatarFill:
        "bg-secondary/15 text-secondary dark:text-secondary-light dark:bg-secondary/20",
      accent: "bg-secondary",
      roleText: "text-secondary dark:text-secondary-light",
      ring: "ring-secondary/40",
    };
  }

  if (isConsultantOfficeRole.value) {
    return {
      icon: "mdi mdi-office-building-outline",
      avatarFill:
        "bg-gradient-to-br from-primary/15 to-secondary/25 text-secondary dark:text-secondary-light",
      accent: "bg-gradient-to-b from-primary to-secondary",
      roleText: "text-secondary dark:text-secondary-light",
      ring: "ring-secondary/40",
    };
  }

  if (isConsultantRole.value) {
    return {
      icon: "mdi mdi-account-tie-outline",
      avatarFill:
        "bg-secondary/15 text-secondary dark:text-secondary-light dark:bg-secondary/20",
      accent: "bg-secondary",
      roleText: "text-secondary dark:text-secondary-light",
      ring: "ring-secondary/40",
    };
  }

  return {
    icon: "mdi mdi-account-circle-outline",
    avatarFill: "bg-gradient-to-br from-primary to-secondary text-white",
    accent: "bg-gradient-to-b from-primary to-secondary",
    roleText: "text-slate-500 dark:text-slate-400",
    ring: "ring-primary/30",
  };
});

const filteredNavItems = computed(() => {
  const items = props.navItems || [];

  return items
    .map((item) => {
      if (!item.children) {
        return item;
      }

      const children = item.children
        .map((child) => {
          if (child.id === "account-settings") {
            return { ...child, route: settingsRoute.value };
          }

          if (
            (isConsultantOnly.value || isOfficeConsulting.value) &&
            child.id === "consultation-create"
          ) {
            return {
              ...child,
              id: "consultation-all",
              icon: "mdi mdi-format-list-bulleted",
              labelKey: "sidebar_item_all_consultations",
              route: "/consultations/all",
            };
          }

          return child;
        })
        .filter((child) => {
          if (isConsultantOnly.value) {
            return (
              child.route !== "/offices" &&
              child.id !== "office-management"
            );
          }

          return true;
        });

      return { ...item, children };
    })
    .filter((item) => {
      if (!item.children) {
        if (isConsultantOnly.value) {
          return (
            item.route !== "/offices" &&
            item.id !== "office-management"
          );
        }
        return true;
      }

      return item.children.length > 0;
    });
});

// ====== Mobile Drawer & Accessibility State ======
const sidebarRef = ref(null);
const isMobileState = ref(false);
let lastActiveElement = null;

// 🔹 Effective Collapsed State: Collapsed mode (w-20 icon-only) applies ONLY on Desktop (>= md)
const isDesktopCollapsed = computed(() => {
  return props.isCollapsed && !isMobileState.value;
});

// 🔹 Responsive Sidebar CSS Classes (RTL & LTR + Desktop Collapsed & Mobile Drawer)
const sidebarClasses = computed(() => {
  const isRtl = lang.value === "ar";

  return [
    // Base layout & background (shrink-0 prevents flexbox layout from squishing sidebar width!)
    "flex flex-col shrink-0 bg-white dark:bg-slate-900 bg-gradient-to-b from-white via-white to-slate-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950",
    "border-e border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white",
    "transition-all duration-300 ease-in-out",

    // 🔹 MOBILE ( < md ): Fixed Drawer Overlay
    "fixed inset-y-0 z-[70] h-dvh max-h-dvh shadow-2xl w-64",
    internalIsOpenMobile.value && "will-change-transform",

    // Mobile directional slide positioning & transforms
    isRtl
      ? [
          "right-0 left-auto", // Fixed to right edge ONLY on mobile
          internalIsOpenMobile.value ? "translate-x-0" : "translate-x-full",
        ]
      : [
          "left-0 right-auto", // Fixed to left edge ONLY on mobile
          internalIsOpenMobile.value ? "translate-x-0" : "-translate-x-full",
        ],

    // 🔹 DESKTOP ( >= md ): Sticky Inline Sidebar
    "md:sticky md:top-0 md:h-screen md:max-h-screen md:overflow-hidden md:translate-x-0 md:z-30 md:shadow-none",
    "md:left-auto md:right-auto", // Reset fixed positioning on desktop for clean sticky flex placement

    // Desktop Width: 80px (w-20) when collapsed, 256px (w-64) when expanded
    props.isCollapsed ? "md:w-20" : "md:w-64",
  ];
});

function checkMobile() {
  if (typeof window !== "undefined") {
    isMobileState.value = window.innerWidth < 768;
  }
}

function closeMobile() {
  internalIsOpenMobile.value = false;
  emit("update:is-open-mobile", false);
  emit("update:isOpenMobile", false);
  emit("update:isSidebarOpenMobile", false);
  emit("update:modelValue", false);
  emit("close-mobile");
  emit("close");
}

// 🔒 Body Scroll Lock on Mobile Drawer Toggle
watch(
  internalIsOpenMobile,
  (val) => {
    if (typeof window === "undefined") return;
    if (val) {
      document.body.style.overflow = "hidden";
      lastActiveElement = document.activeElement;
      setTimeout(() => {
        if (sidebarRef.value) {
          const focusable = sidebarRef.value.querySelector(
            'button, a[href], input, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable) focusable.focus();
        }
      }, 50);
    } else {
      document.body.style.overflow = "";
      if (lastActiveElement && typeof lastActiveElement.focus === "function") {
        lastActiveElement.focus();
      }
    }
  },
  { immediate: true }
);

// ♿ A11y Focus Trapping & Escape Listener
function handleKeydown(e) {
  if (!internalIsOpenMobile.value) return;

  if (e.key === "Escape") {
    closeMobile();
    return;
  }

  if (e.key === "Tab") {
    if (!sidebarRef.value) return;
    const focusables = Array.from(
      sidebarRef.value.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  }
}

// ====== Methods ======
const toggleTheme = () => {
  const html = document.documentElement;
  const nextIsDark = !isDark.value;

  isDark.value = nextIsDark;
  html.classList.toggle("dark", nextIsDark);
  html.classList.toggle("light", !nextIsDark);
};

const toggleGroup = (id) => {
  openGroups[id] = !openGroups[id];
};

function shouldOpenGroup(item) {
  if (!item?.children?.length) return false;
  return item.children.some((child) => {
    if (!child.route) return false;
    return (
      route.path === child.route ||
      route.path.startsWith(`${child.route}/`)
    );
  });
}

function syncOpenGroups() {
  (filteredNavItems.value || []).forEach((item) => {
    if (item.children) {
      const isChildActive = shouldOpenGroup(item);
      if (isChildActive) {
        openGroups[item.id] = true;
      } else if (item.id === "consultations") {
        // Consultations tab is open by default whenever user logs in or visits
        if (openGroups[item.id] === undefined) {
          openGroups[item.id] = true;
        }
      } else {
        openGroups[item.id] = false;
      }
    }
  });
}

const handleItemClick = (item) => {
  if (item.route) {
    router.push(item.route);

    if (isOpenMobile.value) {
      closeMobile();
    }
  }
};

const isActiveRoute = (item) => {
  if (!item.route) return false;
  return route.path === item.route;
};

// ====== Floating Tooltip / Flyout Menu State for Collapsed Mode ======
const activeHoverItem = ref(null);
let closeTimeout = null;

function handleItemMouseEnter(event, item, type = "single") {
  if (!isDesktopCollapsed.value) return;
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
  const el = event.currentTarget;
  if (!el) return;
  const rect = el.getBoundingClientRect();

  const isProfile = type === "profile";
  activeHoverItem.value = {
    id: item.id || (isProfile ? "profile" : item.labelKey),
    label: isProfile ? displayName.value : t(item.labelKey),
    subLabel: isProfile ? displayRole.value : null,
    icon: isProfile ? (avatarSrc.value ? null : roleTheme.value?.icon) : item.icon,
    route: item.route,
    type,
    children: item.children || null,
    targetRect: {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
      height: rect.height,
    },
  };
}

function handleItemMouseLeave() {
  if (!isDesktopCollapsed.value) return;
  closeTimeout = setTimeout(() => {
    activeHoverItem.value = null;
  }, 120);
}

function handleFlyoutMouseEnter() {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
}

function handleFlyoutMouseLeave() {
  closeTimeout = setTimeout(() => {
    activeHoverItem.value = null;
  }, 120);
}

function closeFlyoutAndNavigate() {
  activeHoverItem.value = null;
  if (internalIsOpenMobile.value) {
    closeMobile();
  }
}

const floatingFlyoutStyle = computed(() => {
  if (!activeHoverItem.value || !activeHoverItem.value.targetRect) {
    return { display: "none" };
  }
  const rect = activeHoverItem.value.targetRect;
  const isRtl = lang.value === "ar";
  const offset = 10;

  let top = rect.top + rect.height / 2;
  if (typeof window !== "undefined") {
    if (top > window.innerHeight - 100) {
      top = window.innerHeight - 100;
    }
    if (top < 60) {
      top = 60;
    }
  }

  const pos = {
    position: "fixed",
    top: `${top}px`,
    zIndex: 9999,
  };

  if (isRtl) {
    if (typeof window !== "undefined") {
      pos.right = `${window.innerWidth - rect.left + offset}px`;
    } else {
      pos.right = "80px";
    }
    pos.left = "auto";
  } else {
    pos.left = `${rect.right + offset}px`;
    pos.right = "auto";
  }

  return pos;
});

function handleWindowScroll() {
  activeHoverItem.value = null;
}

// ====== Lifecycle ======
onMounted(() => {
  const html = document.documentElement;
  isDark.value = html.classList.contains("dark");

  checkMobile();
  window.addEventListener("resize", checkMobile, { passive: true });
  window.addEventListener("scroll", handleWindowScroll, { passive: true });

  loadUserFromCookie();
  syncOpenGroups();
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    document.body.style.overflow = "";
    window.removeEventListener("resize", checkMobile);
    window.removeEventListener("scroll", handleWindowScroll);
  }
  if (closeTimeout) {
    clearTimeout(closeTimeout);
  }
});

watch(isDesktopCollapsed, (val) => {
  if (!val) {
    activeHoverItem.value = null;
  }
});

watch(
  () => route.path,
  () => {
    syncOpenGroups();
    activeHoverItem.value = null;
    if (internalIsOpenMobile.value) {
      closeMobile();
    }
  }
);
</script>

<style scoped>
/* 🌿 Soft, Modern Brand Scrollbar for Sidebar (RTL Left-Side Compatible) */
.sidebar-scroll {
  scroll-behavior: smooth;
  overscroll-behavior-y: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(5, 139, 128, 0.45) transparent;
}

/* Force Left Placement in RTL */
:global([dir="rtl"]) .sidebar-scroll,
.sidebar-scroll[dir="rtl"] {
  direction: rtl;
}

:deep(.sidebar-scroll::-webkit-scrollbar),
:global(.sidebar-scroll::-webkit-scrollbar) {
  width: 5px !important;
  height: 5px !important;
}

:deep(.sidebar-scroll::-webkit-scrollbar-button),
:global(.sidebar-scroll::-webkit-scrollbar-button) {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

:deep(.sidebar-scroll::-webkit-scrollbar-track),
:global(.sidebar-scroll::-webkit-scrollbar-track) {
  background: rgba(5, 139, 128, 0.03) !important;
  border-radius: 9999px !important;
}

:deep(.sidebar-scroll::-webkit-scrollbar-thumb),
:global(.sidebar-scroll::-webkit-scrollbar-thumb) {
  background: rgba(5, 139, 128, 0.4) !important;
  border-radius: 9999px !important;
  border: 1px solid transparent !important;
  background-clip: padding-box !important;
  transition: background-color 0.25s ease, box-shadow 0.25s ease !important;
}

:deep(.sidebar-scroll:hover::-webkit-scrollbar-thumb),
:global(.sidebar-scroll:hover::-webkit-scrollbar-thumb),
:deep(.sidebar-scroll::-webkit-scrollbar-thumb:hover),
:global(.sidebar-scroll::-webkit-scrollbar-thumb:hover) {
  background: rgba(5, 139, 128, 0.85) !important;
  box-shadow: 0 0 8px rgba(5, 139, 128, 0.4) !important;
}

/* 🌙 Dark Mode Soft Scrollbar */
:global(html.dark) .sidebar-scroll {
  scrollbar-color: rgba(94, 234, 212, 0.45) transparent;
}

:global(html.dark) :deep(.sidebar-scroll::-webkit-scrollbar-track),
:global(html.dark) :global(.sidebar-scroll::-webkit-scrollbar-track) {
  background: rgba(15, 23, 42, 0.4) !important;
}

:global(html.dark) :deep(.sidebar-scroll::-webkit-scrollbar-thumb),
:global(html.dark) :global(.sidebar-scroll::-webkit-scrollbar-thumb) {
  background: rgba(94, 234, 212, 0.4) !important;
  border: 1px solid transparent !important;
  background-clip: padding-box !important;
}

:global(html.dark) :deep(.sidebar-scroll:hover::-webkit-scrollbar-thumb),
:global(html.dark) :global(.sidebar-scroll:hover::-webkit-scrollbar-thumb),
:global(html.dark) :deep(.sidebar-scroll::-webkit-scrollbar-thumb:hover),
:global(html.dark) :global(.sidebar-scroll::-webkit-scrollbar-thumb:hover) {
  background: rgba(94, 234, 212, 0.9) !important;
  box-shadow: 0 0 10px rgba(94, 234, 212, 0.5) !important;
}
</style>